<template>
  <m-pageframe title="Stock Images">
    <v-container>
        Admin-only library of reusable images hosted by RedChirp. These appear in image pickers for all organizations. Archiving hides an image from pickers without deleting it or breaking existing URLs.

      <v-row class="mb-4" align="center">
        <v-col cols="12" md="3">
          <v-select
            v-model="filter_industry"
            :items="industry_filter_options"
            label="Filter by industry"
            clearable
            @change="fetchList"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-checkbox
            v-model="include_archived"
            label="Include archived"
            @change="fetchList"
          />
        </v-col>
        <v-col cols="12" md="6" class="text-right">
          <v-btn color="primary" @click="openUpload">
            <v-icon left>mdi-upload</v-icon>
            Upload stock image
          </v-btn>
        </v-col>
      </v-row>

      <v-data-table
        :items="items"
        :headers="headers"
        :loading="loading"
        :items-per-page="25"
        class="elevation-1"
      >
        <template v-slot:item.preview_url="{ item }">
          <v-img
            :src="item.preview_url"
            max-width="96"
            max-height="64"
            contain
            style="cursor: pointer;"
            @click="showPreview(item)"
          />
        </template>
        
        <template #item.size_in_bytes="{ item }">
          {{ (item.size_in_bytes / (1024 * 1024)).toFixed(2) }}
        </template>

        <template #item.is_archived="{ item }">
          <v-chip :color="item.is_archived ? 'grey' : 'green'" text-color="white" small>
            {{ item.is_archived ? 'archived' : 'active' }}
          </v-chip>
        </template>

        <template #item.actions="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="openLabelDialog(item)">
                <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                <v-list-item-title>Change Label</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openIndustryDialog(item)">
                <v-list-item-icon><v-icon>mdi-factory</v-icon></v-list-item-icon>
                <v-list-item-title>Change Industries</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openArchiveDialog(item)">
                <v-list-item-icon><v-icon>mdi-archive</v-icon></v-list-item-icon>
                <v-list-item-title>{{ item.is_archived ? 'Unarchive' : 'Archive' }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>

      <m-edit-blobs-dialog
        ref="uploader"
        title="Upload Stock Image"
        :allow_multiple="false"
        :allow_media_assets="false"
        :show_recent_files="false"
        :organization_id="$rc.ORG_ID_RED_CHIRP"
        :show_industry_selector="true"
        purpose="stock-image"
        @ok="afterUpload"
      />

      <v-dialog v-model="preview_dialog">
        <v-card v-if="selected_item">
          <v-card-title class="d-flex justify-space-between align-center">
            <span>{{ selected_item.label }}</span>
            <v-btn icon @click="preview_dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-img :src="selected_item.preview_url" contain />
        </v-card>
      </v-dialog>

      <v-dialog v-model="label_dialog" max-width="400" persistent>
        <v-card v-if="selected_item">
          <v-card-title>Change Label</v-card-title>
          <v-card-text>
            <v-text-field
              v-model="new_label_value"
              label="Image Label"
              autofocus
              @keydown.enter="applyLabelChange"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="label_dialog = false">Cancel</v-btn>
            <v-btn color="primary" text @click="applyLabelChange">Apply</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="industry_dialog" max-width="500">
        <v-card v-if="selected_item">
          <v-card-title>Change Industry Restrictions</v-card-title>
          <v-card-text>
            <v-select
              v-model="new_industry_values"
              :items="$rc.enumChoices('industry_code', false)"
              label="Industries (optional)"
              multiple
              chips
              clearable
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="industry_dialog = false">Cancel</v-btn>
            <v-btn color="primary" text @click="applyIndustryChange">Apply</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="archive_dialog" max-width="450">
        <v-card v-if="selected_item">
          <v-card-title>Confirm Action</v-card-title>
          <v-card-text>
            Are you sure you want to <strong>{{ selected_item.is_archived ? 'unarchive' : 'archive' }}</strong> the image "{{ selected_item.label }}"?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="archive_dialog = false">Cancel</v-btn>
            <v-btn color="primary" text @click="applyArchiveChange">Confirm</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-container>
  </m-pageframe>
</template>

<script>
export default {
  data() {
    return {
      items: [],
      loading: false,
      filter_industry: null,
      include_archived: false,
      
      // State for dialogs
      selected_item: null,
      preview_dialog: false,
      label_dialog: false,
      new_label_value: '',
      industry_dialog: false,
      new_industry_values: [],
      archive_dialog: false,

      headers: [
        { text: 'Preview', value: 'preview_url', sortable: false },
        { text: 'Label', value: 'label' },
        { text: 'Restrictions', value: 'restrictions_text' },
        { text: 'Size (MB)', value: 'size_in_bytes' },
        { text: 'Content Type', value: 'content_type' },
        { text: 'Status', value: 'is_archived' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
    };
  },
  computed: {
    industry_filter_options() {
      return [
        { text: 'All Industries', value: null },
        ...this.$rc.enumChoices('industry_code', false)
      ];
    },
    industry_options() {
      return [
        { text: 'No restriction', value: null },
        ...this.$rc.enumChoices('industry_code', false)
      ];
    }
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    fetchList() {
      this.loading = true;
      this.$rc.rest({
        endpoint: 'LIST: /api/v1/admin/stock-images',
        spinner: false,
        params: {
          industry: this.filter_industry,
          include_archived: this.include_archived,
        },
        on_success: (data) => {
          this.items = (data.results || []).map((x) => {
            const codes = x.restrict_to_industry_codes || [];
            const text = (codes.length === 0)
              ? 'All industries'
              : codes.map((c) => this.$rc.pretty(c, 'enum_industry_code')).filter(Boolean).join(', ');
            return {
              blob_id: x.blob_id,
              label: x.label,
              preview_url: 'https://files.redchirp.com/' + x.object_name,
              is_archived: x.is_archived,
              size_in_bytes: x.size_in_bytes,
              content_type: x.content_type,
              uploaded_time: x.uploaded_time,
              restrict_to_industry_codes: codes,
              restrictions_text: text,
            };
          });
          this.loading = false;
        },
        on_error: () => {
          this.loading = false;
          this.$rc.alert('Error', 'Unable to list stock images');
        },
      });
    },

    openUpload() {
      this.$refs.uploader.showDialog([], true);
    },

    afterUpload() {
      this.fetchList();
    },

    // --- Dialog Openers ---
    showPreview(item) {
      this.selected_item = item;
      this.preview_dialog = true;
    },

    openLabelDialog(item) {
      this.selected_item = item;
      this.new_label_value = item.label;
      this.label_dialog = true;
    },
    
    openIndustryDialog(item) {
      this.selected_item = item;
      this.new_industry_values = (item.restrict_to_industry_codes || []).slice();
      this.industry_dialog = true;
    },

    openArchiveDialog(item) {
      this.selected_item = item;
      this.archive_dialog = true;
    },
    
    // --- Dialog Actions ---
    applyLabelChange() {
      const item = this.selected_item;
      const new_label = this.new_label_value;

      if (!new_label || new_label === item.label) {
        this.label_dialog = false;
        return;
      }
      this.label_dialog = false;

      this.$rc.rest({
        endpoint: 'PATCH: /api/v1/blobs/' + item.blob_id,
        spinner: true,
        params: { new_label },
        on_success: () => {
          const idx = this.items.findIndex(x => x.blob_id === item.blob_id);
          if (idx >= 0) {
            this.$set(this.items, idx, { ...this.items[idx], label: new_label });
          }
        },
        on_error: () => this.$rc.alert('Error', 'Unable to change label'),
      });
    },

    applyIndustryChange() {
      const item = this.selected_item;
      const new_codes = this.new_industry_values || [];
      this.industry_dialog = false;

      this.$rc.rest({
        endpoint: 'PATCH: /api/v1/blobs/' + item.blob_id,
        spinner: true,
        params: { new_industry_codes: new_codes },
        on_success: () => {
          const idx = this.items.findIndex(x => x.blob_id === item.blob_id);
          if (idx >= 0) {
            const updated_item = {
              ...this.items[idx],
              restrict_to_industry_codes: new_codes.slice(),
              restrictions_text: (new_codes.length === 0)
                ? 'All industries'
                : new_codes.map((c) => this.$rc.pretty(c, 'enum_industry_code')).filter(Boolean).join(', '),
            };
            this.$set(this.items, idx, updated_item);
          }
        },
        on_error: () => this.$rc.alert('Error', 'Unable to change industry restrictions'),
      });
    },

    applyArchiveChange() {
        const item = this.selected_item;
        if (!item) return;
        
        const new_archive_state = !item.is_archived;
        this.archive_dialog = false;

        this.$rc.rest({
            endpoint: 'PATCH: /api/v1/admin/stock-images/archive',
            spinner: true,
            params: {
                blob_id: item.blob_id,
                is_archived: new_archive_state,
            },
            on_success: () => {
                this.fetchList();
            },
            on_error: () => this.$rc.alert('Error', 'Unable to change archive state'),
        });
    },
  },
};
</script>

<style scoped>
</style>